<template>
    <div class="automation-block-editor date-format-item">
        <div class="d-flex justify-content-between">
            <CustomPicker v-model="clone.mode" :items="formatTypes" idKey="id" labelKey="label" title="Select Format" />
            <SupsisButton @click="deleteFormatter" danger v-if="deleteEnabled" xxsmall>
                <i class="material-icons">delete</i>
            </SupsisButton>
        </div>

        <div class="editor-row customize-editor-minimize data-items">
            <div class="editor-row-title">Date Input(s)</div>
            <div v-for="(item, index) in clone.inputs" :key="index" class="d-flex gap-5">
                <TiptapEditor
                    placeholder="Input Date"
                    :show-attributes="true"
                    v-model="clone.inputs[index]"
                    :show-emoji="false"
                    automation-mode
                />
                <a @click="() => deleteInput(index)" :disabled="clone.inputs.length <= 1"> sil </a>
            </div>
        </div>
        <div class="d-flex flex-end w-100">
            <SupsisButton @click="addInput" v-if="addInputEnabled()" primary>
                <i class="material-icons">add</i>
                <span>Add Date Input</span>
            </SupsisButton>
        </div>

        <DateAdd v-if="clone.mode === 'date.add'" v-model="clone" />
        <DateSub v-if="clone.mode === 'date.sub'" v-model="clone" />
        <DateStartOf v-if="clone.mode === 'date.startOf'" v-model="clone" />
        <DateEndOf v-if="clone.mode === 'date.endOf'" v-model="clone" />
        <DateIsBefore v-if="clone.mode === 'date.isBefore'" v-model="clone" />
        <DateIsAfter v-if="clone.mode === 'date.isAfter'" v-model="clone" />
        <DateIsBetween v-if="clone.mode === 'date.isBetween'" v-model="clone" />
        <DateIsLeapYear v-if="clone.mode === 'date.isLeapYear'" v-model="clone" />
        <DateIsWeekend v-if="clone.mode === 'date.isWeekend'" v-model="clone" />
        <DateIsTomorrow v-if="clone.mode === 'date.isTomorrow'" v-model="clone" />
        <DateIsYesterday v-if="clone.mode === 'date.isYesterday'" v-model="clone" />
        <DateIsWeekday v-if="clone.mode === 'date.isWeekday'" v-model="clone" />
        <DateIsToday v-if="clone.mode === 'date.isToday'" v-model="clone" />
    </div>
</template>

<script>
import log2 from "../../../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../../../Settings/components/SupsisButton.vue";
import DateAdd from "./formats/DateAdd.vue";
import DateSub from "./formats/DateSub.vue";
import DateStartOf from "./formats/DateStartOf.vue";
import DateEndOf from "./formats/DateEndOf.vue";
import DateIsBefore from "./formats/DateIsBefore.vue";
import DateIsAfter from "./formats/DateIsAfter.vue";
import DateIsBetween from "./formats/DateIsBetween.vue";
import DateIsLeapYear from "./formats/DateIsLeapYear.vue";
import DateIsWeekend from "./formats/DateIsWeekend.vue";
import DateIsTomorrow from "./formats/DateIsTomorrow.vue";
import DateIsYesterday from "./formats/DateIsYesterday.vue";
import DateIsWeekday from "./formats/DateIsWeekday.vue";
import DateIsToday from "./formats/DateIsToday.vue";

const log = log2("DateFormatItem");
export default {
    name: "DateFormatItem",
    components: {
        SupsisButton,
        CustomPicker,
        TiptapEditor,
        DateAdd,
        DateSub,
        DateStartOf,
        DateEndOf,
        DateIsBefore,
        DateIsAfter,
        DateIsBetween,
        DateIsLeapYear,
        DateIsWeekend,
        DateIsTomorrow,
        DateIsYesterday,
        DateIsWeekday,
        DateIsToday,
    },
    props: ["value", "deleteEnabled"],
    data() {
        const clone = _.cloneDeep(this.value) || {
            mode: "date.add",
            inputs: [""],
            add: {},
        };
        return {
            clone,
            singleValueFormats: [
                "date.isLeapYear",
                "date.isWeekend",
                "date.isWeekday",
                "date.isToday",
                "date.isTomorrow",
                "date.isYesterday",
                "date.startOf",
                "date.endOf",
            ],
            formatTypes: [
                { id: "date.add", label: "Date Add", description: "Add parts to a date", maxInputCount: 1 },
                { id: "date.sub", label: "Date Sub", description: "Subtract parts from a date", maxInputCount: 1 },
                { id: "date.diff", label: "Date Diff", description: "Difference between two dates", maxInputCount: 2 },
                { id: "date.format", label: "Date Format", description: "Format a date", maxInputCount: 1 },
                { id: "date.compare", label: "Compare Dates", description: "Compare two dates", maxInputCount: 1 },
                { id: "date.startOf", label: "Start Of", description: "Start of a date", maxInputCount: 1 },
                { id: "date.endOf", label: "End Of", description: "End of a date", maxInputCount: 1 },

                {
                    id: "date.isBefore",
                    label: "Is Before",
                    description: "Check if a date is before another",
                    maxInputCount: 2,
                },
                {
                    id: "date.isAfter",
                    label: "Is After",
                    description: "Check if a date is after another",
                    maxInputCount: 2,
                },
                {
                    id: "date.isBetween",
                    label: "Is Between",
                    description: "Check if a date is between two dates",
                    maxInputCount: 3,
                },
                {
                    id: "date.isLeapYear",
                    label: "Is Leap Year",
                    description: "Check if a date is a leap year",
                    maxInputCount: 1,
                },
                {
                    id: "date.isWeekend",
                    label: "Is Weekend",
                    description: "Check if a date is a weekend",
                    maxInputCount: 1,
                },
                {
                    id: "date.isWeekday",
                    label: "Is Weekday",
                    description: "Check if a date is a weekday",
                    maxInputCount: 1,
                },
                { id: "date.isToday", label: "Is Today", description: "Check if a date is today", maxInputCount: 1 },
                {
                    id: "date.isTomorrow",
                    label: "Is Tomorrow",
                    description: "Check if a date is tomorrow",
                    maxInputCount: 1,
                },
                {
                    id: "date.isYesterday",
                    label: "Is Yesterday",
                    description: "Check if a date is yesterday",
                    maxInputCount: 1,
                },
            ],
        };
    },
    methods: {
        addInput() {
            this.clone.inputs.push("");
        },
        deleteInput(index) {
            this.clone.inputs.splice(index, 1);
        },
        deleteFormatter() {
            this.$emit("delete");
        },
        isSingleValueFormat() {
            return this.singleValueFormats.includes(this.clone.mode);
        },
        addInputEnabled() {
            const inputCount = this.clone.inputs.length;
            const maxInputCount = this.formatTypes.find((x) => x.id === this.clone.mode)?.maxInputCount;
            return inputCount < maxInputCount;
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue, oldValue) {
                const modeChanged = newValue.mode !== oldValue.mode;
                const isSingleValueFormat = this.singleValueFormats.includes(newValue.mode);
                if (modeChanged && isSingleValueFormat) {
                    this.clone.inputs = [newValue.inputs[0]];
                    newValue.inputs = [newValue.inputs[0]];
                }
                if (modeChanged) {
                    const defaultValues = {
                        "date.add": {
                            value: "1",
                            unit: "days",
                            fromFormat: "YYYY-MM-DD",
                            toFormat: "YYYY-MM-DD",
                            fromLocale: "en",
                            toLocale: "en",
                        },
                        "date.sub": {
                            value: "1",
                            unit: "days",
                            fromFormat: "YYYY-MM-DD",
                            toFormat: "YYYY-MM-DD",
                            fromLocale: "en",
                            toLocale: "en",
                        },
                        "date.diff": {
                            value: "1",
                            unit: "days",
                        },
                        "date.format": {
                            fromFormat: "YYYY-MM-DD",
                            toFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            toTimezone: "UTC",
                            fromLocale: "en",
                            toLocale: "en",
                        },
                        "date.compare": {
                            fromFormat: "YYYY-MM-DD",
                            toFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            toTimezone: "UTC",
                            fromLocale: "en",
                            toLocale: "en",
                        },
                        "date.startOf": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                        "date.endOf": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                        "date.isBefore": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                        "date.isAfter": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                            toFormat: "YYYY-MM-DD",
                            toTimezone: "UTC",
                            toLocale: "en",
                        },
                        "date.isBetween": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                            toFormat: "YYYY-MM-DD",
                            toTimezone: "UTC",
                            toLocale: "en",
                        },
                        "date.isLeapYear": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                        "date.isWeekend": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                        "date.isWeekday": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                        "date.isToday": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                        },
                        "date.isTomorrow": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                        "date.isYesterday": {
                            fromFormat: "YYYY-MM-DD",
                            fromTimezone: "UTC",
                            fromLocale: "en",
                        },
                    };
                    if (newValue.mode === "date.add") {
                        newValue.add = defaultValues["date.add"];
                    } else if (newValue.mode === "date.sub") {
                        newValue.sub = defaultValues["date.sub"];
                    } else if (newValue.mode === "date.diff") {
                        newValue.diff = defaultValues["date.diff"];
                    } else if (newValue.mode === "date.format") {
                        newValue.format = defaultValues["date.format"];
                    } else if (newValue.mode === "date.compare") {
                        newValue.compare = defaultValues["date.compare"];
                    } else if (newValue.mode === "date.startOf") {
                        newValue.startOf = defaultValues["date.startOf"];
                    } else if (newValue.mode === "date.endOf") {
                        newValue.endOf = defaultValues["date.endOf"];
                    } else if (newValue.mode === "date.isBefore") {
                        newValue.isBefore = defaultValues["date.isBefore"];
                    } else if (newValue.mode === "date.isAfter") {
                        newValue.isAfter = defaultValues["date.isAfter"];
                    } else if (newValue.mode === "date.isBetween") {
                        newValue.isBetween = defaultValues["date.isBetween"];
                    } else if (newValue.mode === "date.isLeapYear") {
                        newValue.isLeapYear = defaultValues["date.isLeapYear"];
                    } else if (newValue.mode === "date.isWeekend") {
                        newValue.isWeekend = defaultValues["date.isWeekend"];
                    } else if (newValue.mode === "date.isWeekday") {
                        newValue.isWeekday = defaultValues["date.isWeekday"];
                    } else if (newValue.mode === "date.isToday") {
                        newValue.isToday = defaultValues["date.isToday"];
                    } else if (newValue.mode === "date.isTomorrow") {
                        newValue.isTomorrow = defaultValues["date.isTomorrow"];
                    } else if (newValue.mode === "date.isYesterday") {
                        newValue.isYesterday = defaultValues["date.isYesterday"];
                    }
                    this.clone = _.cloneDeep(newValue);
                }

                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.automation-block-editor.date-format-item {
    padding: 20px;
    display: flex;
    gap: 20px;
    flex-direction: column;

    .editor-row {
        width: 100%;

        &.customize-editor-minimize {
            display: flex;
            flex-direction: column;
            gap: 5px;
        }

        small.description {
            font-size: 10px;
            color: var(--color-text-weak-default);
        }

        .editor-row-title {
            font-weight: bold;
            font-size: 16px;
        }
    }
}
</style>
